import React, { useState } from "react";
import emailjs from "@emailjs/browser";
import { ArrowRight, CalendarClock, CheckCircle2, Mail, MessageSquareText } from "lucide-react";
import Reveal from "./Reveal";

const SIGNIN_URL = process.env.REACT_APP_SIGNIN_URL;
// EmailJS sends through the techfrigate@gmail.com Gmail service connected in the EmailJS dashboard.
const EMAILJS = {
  serviceId: process.env.REACT_APP_EMAILJS_SERVICE_ID || "service_f3g0v26",
  templateId: process.env.REACT_APP_EMAILJS_TEMPLATE_ID || "template_dtugkcg",
  publicKey: process.env.REACT_APP_EMAILJS_PUBLIC_KEY || "HmJqR3KzE79X0K3BU",
};
const EMPTY = { name: "", email: "", phone: "", organisation: "", size: "Single clinic", message: "" };
const input =
  "block w-full rounded-xl border border-white/15 bg-white/10 px-4 py-3 text-sm text-white placeholder:text-slate-400 focus:border-care-300 focus:outline-none focus:ring-2 focus:ring-care-300/30";

const Contact = () => {
  const [form, setForm] = useState(EMPTY);
  const [status, setStatus] = useState("idle");
  const set = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    try {
      await emailjs.send(
        EMAILJS.serviceId,
        EMAILJS.templateId,
        {
          to_name: "Health Frigate",
          to_email: "techfrigate@gmail.com",
          from_name: form.name,
          from_email: form.email,
          reply_to: form.email,
          organisation: form.organisation,
          phone: form.phone,
          size: form.size,
          message: [
            `Demo request from ${form.organisation || "—"} (${form.size})`,
            `Phone: ${form.phone || "—"}`,
            "",
            form.message || "(no message)",
          ].join("\n"),
        },
        EMAILJS.publicKey
      );
      setStatus("success");
      setForm(EMPTY);
    } catch (err) {
      // e.g. 412 "Gmail_API: Invalid grant" means the Gmail account must be reconnected in EmailJS.
      console.error("Demo request failed", err?.status, err?.text);
      setStatus("error");
    }
  };

  return (
    <section id="contact" className="py-16 sm:py-24">
      <div className="container-x">
        <div className="relative overflow-hidden rounded-[2rem] bg-ink-900 px-6 py-14 sm:px-12 lg:px-16">
          <div className="pointer-events-none absolute inset-0">
            <div className="absolute -right-24 -top-24 h-96 w-96 rounded-full bg-brand-600/40 blur-3xl" />
            <div className="absolute -bottom-32 left-10 h-80 w-80 rounded-full bg-care-500/25 blur-3xl" />
            <div className="bg-grid absolute inset-0 opacity-[0.06] [filter:invert(1)]" />
          </div>
          <div className="relative grid gap-12 lg:grid-cols-2 lg:items-center">
            <Reveal>
              <h2 className="text-3xl font-extrabold tracking-tight !text-white sm:text-4xl lg:text-5xl">
                See Health Frigate running <span className="bg-gradient-to-r from-care-300 to-brand-300 bg-clip-text text-transparent">your hospital</span>
              </h2>
              <p className="mt-5 text-lg text-slate-300">
                Get a 30-minute walkthrough tailored to your clinic or hospital — appointments, video consults, IPD and billing with your own services and prices.
              </p>
              <ul className="mt-8 space-y-4 text-slate-300">
                {[
                  [CalendarClock, "A live demo at a time that suits you"],
                  [MessageSquareText, "Help configuring locations, rosters and price lists"],
                  [Mail, "Or write to us at techfrigate@gmail.com"],
                ].map(([Icon, t]) => (
                  <li key={t} className="flex items-center gap-3">
                    <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-white/10 text-care-300"><Icon className="h-5 w-5" /></span>
                    {t}
                  </li>
                ))}
              </ul>
              <p className="mt-8 text-sm text-slate-400">
                Already a customer?{" "}
                <a href={SIGNIN_URL} className="font-semibold text-white underline-offset-4 hover:underline">Sign in to your portal →</a>
              </p>
            </Reveal>

            <Reveal delay={0.1}>
              {status === "success" ? (
                <div className="rounded-2xl border border-white/10 bg-white/5 p-8 text-center backdrop-blur">
                  <CheckCircle2 className="mx-auto h-12 w-12 text-care-300" />
                  <h3 className="mt-4 text-xl font-bold !text-white">Thanks — we'll be in touch shortly</h3>
                  <p className="mt-2 text-slate-300">Our team will reach out to schedule your demo.</p>
                  <button type="button" onClick={() => setStatus("idle")} className="mt-6 text-sm font-semibold text-care-300 hover:underline">Send another request</button>
                </div>
              ) : (
                <form onSubmit={submit} className="space-y-4 rounded-2xl border border-white/10 bg-white/5 p-6 backdrop-blur sm:p-8">
                  <div className="grid gap-4 sm:grid-cols-2">
                    <label className="block">
                      <span className="sr-only">Your name</span>
                      <input required name="name" value={form.name} onChange={set} placeholder="Your name" className={input} />
                    </label>
                    <label className="block">
                      <span className="sr-only">Work email</span>
                      <input required type="email" name="email" value={form.email} onChange={set} placeholder="Work email" className={input} />
                    </label>
                    <label className="block">
                      <span className="sr-only">Phone</span>
                      <input name="phone" value={form.phone} onChange={set} placeholder="Phone (optional)" className={input} />
                    </label>
                    <label className="block">
                      <span className="sr-only">Hospital or clinic</span>
                      <input required name="organisation" value={form.organisation} onChange={set} placeholder="Hospital / clinic name" className={input} />
                    </label>
                  </div>
                  <label className="block">
                    <span className="mb-1.5 block text-xs font-medium text-slate-400">Organisation size</span>
                    <select name="size" value={form.size} onChange={set} className={`${input} [&>option]:text-ink-900`}>
                      {["Single clinic", "2–5 clinics", "Hospital (up to 100 beds)", "Hospital (100+ beds)", "Hospital network"].map((s) => (
                        <option key={s}>{s}</option>
                      ))}
                    </select>
                  </label>
                  <label className="block">
                    <span className="sr-only">Message</span>
                    <textarea name="message" rows={3} value={form.message} onChange={set} placeholder="What would you like to see? (optional)" className={input} />
                  </label>
                  <button type="submit" disabled={status === "sending"} className="btn-primary w-full !bg-care-500 py-3.5 text-base !shadow-none hover:!bg-care-600 disabled:opacity-60">
                    {status === "sending" ? "Sending…" : (<>Request my demo <ArrowRight className="h-5 w-5" /></>)}
                  </button>
                  {status === "error" && <p className="text-center text-sm text-rose-300">Something went wrong. Please try again or email techfrigate@gmail.com.</p>}
                </form>
              )}
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
